import React, {
	useState,
	useRef,
	useEffect,
	useMemo,
	useCallback,
} from "react";
import { FormGroup } from "react-bootstrap";
import { FiSearch, FiCheck } from "react-icons/fi";
import { HiOutlineMagnifyingGlass } from "react-icons/hi2";
import "./PlanEmployeesMultiSelect.scss";

function normId(id) {
	if (id === null || id === undefined) return null;
	const s = String(id).trim();
	if (s === "" || s === "-1") return null;
	return s;
}

function normalizeIds(value) {
	if (!Array.isArray(value)) return [];
	return value.map(normId).filter(Boolean);
}

function formatCountLabel(label, count) {
	if (!label) return "";
	return String(label).replace("{{count}}", String(count));
}

function employeeDisplayName(employee) {
	if (!employee) return "";
	return `${employee.name || ""} ${employee.surname || ""}`.trim();
}

/**
 * Multi-selector de empleados para filtrar una planificación.
 * A diferencia de EmployeeSelectSearch, trabaja sobre una lista local
 * (los empleados ya asignados al plan) y no realiza llamadas al backend.
 *
 * @param {Array}    employees   — lista de empleados (`{ id, name, surname, email?, code? }`).
 * @param {Array}    value       — ids seleccionados (array de strings/numbers).
 * @param {function} onChange    — `(ids: string[]) => void` cuando cambia la selección.
 * @param {string}   [placeholder]
 * @param {string}   [allLabel]
 * @param {string}   [searchPlaceholder]
 * @param {string}   [clearLabel]
 * @param {string}   [noResultsLabel]
 * @param {string}   [selectedCountLabel]
 * @param {string}   [hint]
 * @param {string}   [variant]   — `default` (filtro compacto) o `panel` (formulario).
 * @param {string}   [className]
 * @param {boolean}  [disabled]
 * @param {string}   [controlId]
 */
const PlanEmployeesMultiSelect = ({
	employees = [],
	value,
	onChange,
	placeholder = "Filtrar por empleado",
	allLabel = "Todos los empleados",
	searchPlaceholder = "Nombre, apellido, email o código…",
	clearLabel = "Limpiar selección",
	noResultsLabel = "No se encontraron empleados",
	selectedCountLabel = "{{count}} empleados seleccionados",
	hint,
	variant = "default",
	className = "",
	disabled = false,
	controlId,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [searchInput, setSearchInput] = useState("");
	const dropdownRef = useRef(null);
	const isPanel = variant === "panel";

	const selectedIds = useMemo(() => normalizeIds(value), [value]);

	const employeesById = useMemo(() => {
		const map = new Map();
		(employees || []).forEach((e) => {
			if (e && e.id !== undefined && e.id !== null) {
				map.set(String(e.id), e);
			}
		});
		return map;
	}, [employees]);

	const selectedObjects = useMemo(() => {
		return selectedIds.map((id) => employeesById.get(id)).filter(Boolean);
	}, [selectedIds, employeesById]);

	const filteredEmployees = useMemo(() => {
		const q = searchInput.trim().toLowerCase();
		if (!q) return employees || [];
		return (employees || []).filter((e) => {
			const name = (e.name || "").toLowerCase();
			const surname = (e.surname || "").toLowerCase();
			const email = (e.email || "").toLowerCase();
			const code = (e.code || "").toString().toLowerCase();
			return (
				name.includes(q) ||
				surname.includes(q) ||
				email.includes(q) ||
				code.includes(q) ||
				`${name} ${surname}`.includes(q)
			);
		});
	}, [employees, searchInput]);

	useEffect(() => {
		const onDoc = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsOpen(false);
				setSearchInput("");
			}
		};
		document.addEventListener("mousedown", onDoc);
		return () => document.removeEventListener("mousedown", onDoc);
	}, []);

	const triggerLabel = useMemo(() => {
		if (selectedIds.length === 0) {
			return <span className="placeholder">{placeholder || allLabel}</span>;
		}
		if (isPanel) {
			return (
				<span className="selected-item">
					{selectedObjects.map(employeeDisplayName).join(", ")}
				</span>
			);
		}
		if (selectedObjects.length === 1) {
			const e = selectedObjects[0];
			return (
				<span className="selected-item">
					{employeeDisplayName(e)}
					{e.code ? ` (${e.code})` : ""}
				</span>
			);
		}
		return (
			<span className="selected-count">
				{formatCountLabel(selectedCountLabel, selectedIds.length)}
			</span>
		);
	}, [
		selectedIds,
		selectedObjects,
		placeholder,
		allLabel,
		selectedCountLabel,
		isPanel,
	]);

	const handleToggleOpen = () => {
		if (disabled) return;
		setIsOpen((o) => !o);
		if (isOpen) setSearchInput("");
	};

	const handleToggleEmployee = useCallback(
		(emp) => {
			const idStr = String(emp.id);
			const set = new Set(selectedIds);
			if (set.has(idStr)) set.delete(idStr);
			else set.add(idStr);
			onChange(Array.from(set));
		},
		[selectedIds, onChange]
	);

	const handleClear = (event) => {
		if (event) event.stopPropagation();
		onChange([]);
	};

	const isSelected = (id) => selectedIds.includes(String(id));

	const renderSearch = () => (
		<div className={isPanel ? "search-wrap" : undefined}>
			<input
				type="text"
				placeholder={searchPlaceholder}
				value={searchInput}
				onChange={(e) => setSearchInput(e.target.value)}
				className="search-input"
				onClick={(e) => e.stopPropagation()}
				onKeyDown={(e) => {
					if (e.key === "Enter") e.preventDefault();
				}}
				autoFocus
			/>
			{isPanel && <FiSearch aria-hidden="true" />}
		</div>
	);

	return (
		<FormGroup
			className={`plan-employees-multiselect ${
				isPanel ? "plan-employees-multiselect--panel" : ""
			} ${className}`}
			style={isPanel ? undefined : { position: "relative" }}
			controlId={controlId}
		>
			<div
				className={`custom-select-container ${isOpen ? "is-open" : ""}`}
				ref={dropdownRef}
			>
				<div
					className={`custom-select select-control ${isOpen ? "open" : ""} ${
						disabled ? "disabled" : ""
					}`}
					onClick={handleToggleOpen}
					onKeyDown={(e) => {
						if (e.key === "Enter" || e.key === " ") {
							e.preventDefault();
							handleToggleOpen();
						}
					}}
					role="button"
					tabIndex={disabled ? -1 : 0}
					aria-expanded={isOpen}
					aria-haspopup="listbox"
				>
					<div className="selected-items">{triggerLabel}</div>
					<HiOutlineMagnifyingGlass
						className="select-trailing-icon"
						size={18}
						aria-hidden="true"
					/>
				</div>

				{isOpen && (
					<div className="dropdown-menu">
						{isPanel ? (
							<div className="dropdown-header">{renderSearch()}</div>
						) : (
							<div className="dropdown-header">
								{selectedIds.length > 0 && (
									<button
										type="button"
										className="clear-all-btn"
										onClick={handleClear}
									>
										{clearLabel}
									</button>
								)}
								{renderSearch()}
							</div>
						)}

						<div className="dropdown-options" role="listbox" aria-multiselectable="true">
							{filteredEmployees.map((employee) => {
								const sel = isSelected(employee.id);
								return (
									<div
										key={employee.id}
										className={`dropdown-option ${sel ? "selected" : ""}`}
										onClick={() => handleToggleEmployee(employee)}
										role="option"
										aria-selected={sel}
									>
										{isPanel && (
											<span
												className={`option-checkbox ${sel ? "checked" : ""}`}
												aria-hidden="true"
											>
												{sel ? <FiCheck /> : null}
											</span>
										)}
										<div className="option-content">
											<span className="employee-name">
												{employeeDisplayName(employee)}
											</span>
											{!isPanel &&
												(employee.code ? (
													<span className="employee-code">
														({employee.code})
													</span>
												) : employee.email ? (
													<span className="employee-code">{employee.email}</span>
												) : null)}
										</div>
										{!isPanel && sel ? (
											<div className="checkmark">✓</div>
										) : null}
									</div>
								);
							})}

							{filteredEmployees.length === 0 && (
								<div className="no-results">{noResultsLabel}</div>
							)}
						</div>

						{isPanel && (
							<div className="dropdown-footer">
								<span className="dropdown-footer__count">
									{formatCountLabel(selectedCountLabel, selectedIds.length)}
								</span>
								{selectedIds.length > 0 && (
									<button
										type="button"
										className="clear-all-btn"
										onClick={handleClear}
									>
										{clearLabel}
									</button>
								)}
							</div>
						)}
					</div>
				)}
			</div>
			{isPanel && hint && <p className="plan-copy-hint">{hint}</p>}
		</FormGroup>
	);
};

export default PlanEmployeesMultiSelect;
